// ReservasPage.tsx — Panel unificado de Citas y Ventas de Cafetería (con paginación)
import Loading from '../../../components/Loading';
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { fetchCitas, Cita } from '../../../services/citasService';
import { fetchVentasCafeteria, VentaCafeteria } from '../../../services/ventaCafeteriaService';
import { useFilter } from '../../../hooks/useFilter';
import {
    Calendar, Clock, User, Building2, CheckCircle2,
    XCircle, AlertCircle, Users, FileText,
    Eye, Home, ChevronRight, Search,
    ShoppingBag, Coffee, Receipt
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ReservasFilter, { ReservasFilterState, ReservasFilterOptions } from '../../../components/Filter/ReservasFilter';
import { useWebSocketCitas } from '../../../hooks/useWebSocket';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';
import Pagination from '../../../components/Pagination/Pagination'; // <-- nuevo import
// La oficialización de códigos de reserva (sesión POS explícita + operador
// identificado por PIN) vive exclusivamente en el navbar del POS
// (``NavbarPos`` -> ``SolicitudReservaPOSModal``). Este panel administrativo no
// tiene sesión ni grant de operador, así que no ofrece esa acción.

// ─────────────────────────────────────────────
// Helpers de formato (sin cambios)
// ─────────────────────────────────────────────
const formatDate = (d?: string | null) => {
    if (!d) return 'No programada';
    const p = d.split('-');
    if (p.length === 3) {
        const date = new Date(+p[0], +p[1] - 1, +p[2]);
        if (!isNaN(date.getTime())) return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    try {
        const date = new Date(d);
        return isNaN(date.getTime()) ? 'Fecha inválida' : date.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return 'Fecha inválida'; }
};

const formatTime = (t?: string | null) => { if (!t) return 'Sin hora'; try { return t.substring(0, 5); } catch { return 'Hora inválida'; } };
const formatCurrency = (a: number) => new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB' }).format(a);

const colors = {
    brand: '#61257b',         // Púrpura corporativo primario
    brandDark: '#3d1250',     // Púrpura oscuro
    brandLight: '#8b3fa8',    // Púrpura suave
    accent1: '#c1348b',       // Magenta / Acento principal
    accent2: '#ea8406',       // Naranja cálido
    accent3: '#ffd97c',       // Amarillo dorado
    primary: '#61257b',
    primaryDark: '#3d1250',
    secondary: '#c1348b',
    secondaryLight: '#059669',// Esmeralda / Éxito
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
.rp-page { font-family:'Plus Jakarta Sans',sans-serif; }
@keyframes rpFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.rp-fadeup   { animation:rpFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.rp-fadeup-1 { animation:rpFadeUp .5s .06s cubic-bezier(.16,1,.3,1) both; }
.rp-fadeup-2 { animation:rpFadeUp .5s .12s cubic-bezier(.16,1,.3,1) both; }
@keyframes spin { to{transform:rotate(360deg);} }
.rp-card {
    background:#fff;border-radius:18px;border:1.5px solid #f1f5f9;
    box-shadow:0 2px 14px rgba(0,0,0,.04);overflow:hidden;
    transition:all .3s cubic-bezier(.16,1,.3,1);
    display:flex;flex-direction:column;
}
.rp-card:hover{transform:translateY(-3px);box-shadow:0 12px 40px rgba(0,0,0,.08);border-color:#e2e8f0;}
.rp-btn-detail {
    display:inline-flex;align-items:center;justify-content:center;gap:6px;
    flex:1;padding:9px 16px;border-radius:10px;font-size:.82rem;font-weight:700;
    background:linear-gradient(135deg,${colors.brand},${colors.accent1});color:#fff;
    text-decoration:none;border:none;transition:all .2s;
}
.rp-btn-detail:hover{transform:translateY(-1px);box-shadow:0 4px 14px rgba(97,37,123,.35);}
.rp-btn-pagos {
    display:inline-flex;align-items:center;justify-content:center;gap:6px;
    padding:9px 14px;border-radius:10px;font-size:.82rem;font-weight:700;
    background:#fdf2f8;color:#9d174d;border:1.5px solid #fbcfe8;
    text-decoration:none;transition:all .2s;
}
.rp-btn-pagos:hover{background:#fce7f3;transform:translateY(-1px);}
/* Botón para ventas – mismo estilo que pagos pero otro color */
.rp-btn-venta {
    display:inline-flex;align-items:center;justify-content:center;gap:6px;
    padding:9px 14px;border-radius:10px;font-size:.82rem;font-weight:700;
    background:#fff7ed;color:#c2410c;border:1.5px solid #fed7aa;
    text-decoration:none;transition:all .2s;
}
.rp-btn-venta:hover{background:#ffedd5;transform:translateY(-1px);}
.custom-scroll::-webkit-scrollbar{height:6px;width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background-color:rgba(97,37,123,0.25);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background-color:rgba(97,37,123,0.5);}
`;

// ─────────────────────────────────────────────
// Badge helpers
// ─────────────────────────────────────────────
type BadgeStyle = { background: string; color: string; border: string; };

const estadoBadge = (estado: string): BadgeStyle => {
    switch (estado.toLowerCase()) {
        case 'confirmada': case 'completada': return { background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' };
        case 'pendiente': return { background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' };
        case 'cancelada': return { background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3' };
        case 'en_proceso': case 'parcial': return { background: '#fdf4ff', color: '#86198f', border: '1px solid #f5d0fe' };
        default: return { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
    }
};

const estadoPagoBadge = (ep: string): BadgeStyle => {
    switch (ep.toLowerCase()) {
        case 'pagado': case 'completado': case 'pagado completo': return { background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' };
        case 'pendiente': return { background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' };
        case 'parcial': return { background: '#fdf4ff', color: '#86198f', border: '1px solid #f5d0fe' };
        case 'cancelado': return { background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3' };
        default: return { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
    }
};

const ejecucionBadge = (e: string): BadgeStyle => {
    switch (e.toLowerCase()) {
        case 'porcomenzar': return { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' };
        case 'encurso': return { background: '#f0fdfa', color: '#0f766e', border: '1px solid #99f6e4' };
        case 'finalizada': return { background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' };
        case 'cancelada': return { background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3' };
        default: return { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
    }
};

const getEjecucionIcon = (e: string) => {
    switch (e.toLowerCase()) {
        case 'porcomenzar': return <Clock size={11} />;
        case 'encurso': return <AlertCircle size={11} />;
        case 'finalizada': return <CheckCircle2 size={11} />;
        case 'cancelada': return <XCircle size={11} />;
        default: return <AlertCircle size={11} />;
    }
};

const getEstadoIcon = (e: string) => {
    switch (e.toLowerCase()) {
        case 'confirmada': case 'completada': return <CheckCircle2 size={11} />;
        case 'cancelada': return <XCircle size={11} />;
        default: return <AlertCircle size={11} />;
    }
};

// ─────────────────────────────────────────────
// Tarjeta para Cita (sin cambios)
// ─────────────────────────────────────────────
const CitaCard = React.memo(({ cita }: { cita: Cita }) => {
    const eb = estadoBadge(cita.estado || '');
    const epb = estadoPagoBadge(cita.estado_pago || '');
    const topColor = cita.estado_pago === 'pagado completo' ? colors.secondaryLight : cita.estado === 'cancelada' ? colors.primary : colors.secondary;

    return (
        <div className="rp-card">
            <div style={{ height: '3px', background: topColor, flexShrink: 0 }} />
            <div style={{ padding: '18px 20px 20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '.78rem', fontWeight: '800', color: '#94a3b8', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '4px' }}>
                            {cita.codigo || `CITA-${cita.id}`}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', fontSize: '.68rem', fontWeight: '700', ...eb }}>
                                {getEstadoIcon(cita.estado || '')} {cita.estado || 'Sin estado'}
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', fontSize: '.68rem', fontWeight: '700', ...epb }}>
                                {cita.estado_pago || 'Sin pago'}
                            </span>
                            {cita.estado_ejecucion && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', fontSize: '.68rem', fontWeight: '700', ...ejecucionBadge(cita.estado_ejecucion) }}>
                                    {getEjecucionIcon(cita.estado_ejecucion)} {cita.estado_ejecucion}
                                </span>
                            )}
                        </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <p style={{ fontSize: '.68rem', color: '#94a3b8', fontWeight: '500' }}>Total</p>
                        <p style={{ fontSize: '1rem', fontWeight: '900', color: '#0f172a' }}>{formatCurrency(cita.total)}</p>
                        {cita.monto_pagado !== undefined && (
                            <p style={{ fontSize: '.72rem', fontWeight: '700', color: cita.monto_pagado >= cita.total ? colors.secondaryLight : colors.accentDark }}>
                                Pag: {formatCurrency(cita.monto_pagado)}
                            </p>
                        )}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: `${colors.secondary}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <User size={14} color={colors.secondary} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '.85rem', fontWeight: '700', color: '#0f172a' }}>
                            {cita.cliente_info ? `${cita.cliente_info.first_name} ${cita.cliente_info.last_name}` : 'Cliente no especificado'}
                        </p>
                        {cita.cliente_info?.email && (
                            <p style={{ fontSize: '.72rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cita.cliente_info.email}</p>
                        )}
                        {cita.cliente_info && (
                            <div style={{ marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                {cita.cliente_info.declaracion_jurada_firmada ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 6px', borderRadius: '5px', fontSize: '.64rem', fontWeight: '700', background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>
                                        <CheckCircle2 size={10} /> Dec. Firmada
                                    </span>
                                ) : (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 6px', borderRadius: '5px', fontSize: '.64rem', fontWeight: '700', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                                        <AlertCircle size={10} /> Dec. Pendiente
                                    </span>
                                )}
                            </div>
                        )}
                        {cita.clientes_asociados_info && cita.clientes_asociados_info.length > 0 && (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px', padding: '2px 7px', borderRadius: '6px', background: `${colors.secondaryLight}10`, border: `1px solid ${colors.secondaryLight}25` }}>
                                <Users size={10} color={colors.secondaryLight} />
                                <span style={{ fontSize: '.68rem', fontWeight: '700', color: '#065f46' }}>+{cita.clientes_asociados_info.length} asociado{cita.clientes_asociados_info.length > 1 ? 's' : ''}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Calendar size={14} color="#64748b" />
                    </div>
                    <div>
                        <p style={{ fontSize: '.83rem', fontWeight: '600', color: '#0f172a' }}>
                            {formatDate(cita.fecha_inicio)}
                            {cita.fecha_fin && cita.fecha_fin !== cita.fecha_inicio && (
                                <span style={{ color: '#94a3b8' }}> → {formatDate(cita.fecha_fin)}</span>
                            )}
                        </p>
                        <p style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '.72rem', color: '#94a3b8' }}>
                            <Clock size={10} /> {formatTime(cita.hora_inicio)}{cita.hora_fin && ` - ${formatTime(cita.hora_fin)}`}
                        </p>
                    </div>
                </div>
                {cita.sucursal_info && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Building2 size={14} color="#64748b" />
                        </div>
                        <p style={{ fontSize: '.83rem', fontWeight: '600', color: '#0f172a' }}>{cita.sucursal_info.nombre}</p>
                    </div>
                )}
                <div style={{ display: 'flex', gap: '8px', paddingTop: '10px', borderTop: '1.5px solid #f8fafc', marginTop: 'auto' }}>
                    <Link to={`/reservas/detalle/${cita.id}`} className="rp-btn-detail">
                        <Eye size={14} /> Ver detalle
                    </Link>
                    <Link to={`/historial-pagos/cita/${cita.id}`} className="rp-btn-pagos">
                        <FileText size={14} /> Pagos
                    </Link>
                </div>
            </div>
        </div>
    );
}, (p, n) => (
    p.cita.id === n.cita.id &&
    p.cita.estado === n.cita.estado &&
    p.cita.estado_pago === n.cita.estado_pago &&
    p.cita.estado_ejecucion === n.cita.estado_ejecucion &&
    p.cita.total === n.cita.total &&
    p.cita.monto_pagado === n.cita.monto_pagado &&
    p.cita.fecha_inicio === n.cita.fecha_inicio &&
    p.cita.fecha_fin === n.cita.fecha_fin
));

// ─────────────────────────────────────────────
// NUEVA Tarjeta para Venta de Cafetería (sin cambios)
// ─────────────────────────────────────────────
const VentaCard = React.memo(({ venta }: { venta: VentaCafeteria }) => {
    const epb = estadoPagoBadge(venta.estado_pago);
    const topColor = venta.estado_pago === 'pagado' ? colors.secondaryLight : venta.estado_pago === 'cancelado' ? colors.primary : colors.accentDark;

    return (
        <div className="rp-card">
            <div style={{ height: '3px', background: topColor, flexShrink: 0 }} />
            <div style={{ padding: '18px 20px 20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: `${colors.accent}20`, color: colors.accentDark, padding: '2px 7px', borderRadius: '5px', fontSize: '.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                                <ShoppingBag size={12} /> VENTA
                            </span>
                        </div>
                        <p style={{ fontSize: '.78rem', fontWeight: '800', color: '#94a3b8', letterSpacing: '.06em', textTransform: 'uppercase' }}>
                            {venta.codigo || `VTA-${venta.id}`}
                        </p>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <p style={{ fontSize: '.68rem', color: '#94a3b8', fontWeight: '500' }}>Total</p>
                        <p style={{ fontSize: '1rem', fontWeight: '900', color: '#0f172a' }}>{formatCurrency(parseFloat(venta.total))}</p>
                    </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', fontSize: '.68rem', fontWeight: '700', ...epb }}>
                        {venta.estado_pago === 'pagado' ? <CheckCircle2 size={11} /> : venta.estado_pago === 'cancelado' ? <XCircle size={11} /> : <AlertCircle size={11} />}
                        {venta.estado_pago}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', fontSize: '.68rem', fontWeight: '700', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>
                        <Receipt size={11} /> {venta.tipo_comprobante}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: `${colors.accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <User size={14} color={colors.accentDark} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '.85rem', fontWeight: '700', color: '#0f172a' }}>
                            {venta.cliente_info ? `${venta.cliente_info.first_name} ${venta.cliente_info.last_name}` : 'Cliente no registrado'}
                        </p>
                        <p style={{ fontSize: '.72rem', color: '#94a3b8' }}>
                            {venta.detalles.length} producto{venta.detalles.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Calendar size={14} color="#64748b" />
                    </div>
                    <p style={{ fontSize: '.83rem', fontWeight: '600', color: '#0f172a' }}>
                        {formatDate(venta.fecha_venta)}
                    </p>
                </div>

                {venta.observaciones && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Coffee size={14} color="#64748b" />
                        </div>
                        <p style={{ fontSize: '.78rem', color: '#475569', lineHeight: 1.3 }}>
                            {venta.observaciones}
                        </p>
                    </div>
                )}

                <div style={{ display: 'flex', gap: '8px', paddingTop: '10px', borderTop: '1.5px solid #f8fafc', marginTop: 'auto' }}>
                    <Link to={`/ventas/detalle/${venta.id}`} className="rp-btn-detail" style={{ background: `linear-gradient(135deg,${colors.accentDark},#b45309)` }}>
                        <Eye size={14} /> Ver detalle
                    </Link>
                    <Link to={`/historial-pagos/venta/${venta.id}`} className="rp-btn-venta">
                        <FileText size={14} /> Pagos
                    </Link>
                </div>
            </div>
        </div>
    );
}, (p, n) => (
    p.venta.id === n.venta.id &&
    p.venta.estado_pago === n.venta.estado_pago &&
    p.venta.total === n.venta.total &&
    p.venta.fecha_venta === n.venta.fecha_venta
));

// ─────────────────────────────────────────────
// Tipos unificados
// ─────────────────────────────────────────────
type ExtendedCita = Cita & { type: 'cita' };
type ExtendedVenta = VentaCafeteria & { type: 'venta' };
type UnifiedItem = ExtendedCita | ExtendedVenta;

// ─────────────────────────────────────────────
// PÁGINA PRINCIPAL (con paginación agregada)
// ─────────────────────────────────────────────
const ReservasPage: React.FC = () => {
    const [citas, setCitas] = useState<Cita[]>([]);
    const [ventas, setVentas] = useState<VentaCafeteria[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const initialLoadRef = useRef(false);

    const { user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('cita', userLevelId);

    const [tipoFilter, setTipoFilter] = useState<'todos' | 'citas' | 'ventas'>('todos');
    const [filterState, setFilterState] = useState<ReservasFilterState>({
        searchTerm: '', sucursalFilter: null, estadoFilter: null, estadoPagoFilter: null
    });
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Paginación – siguiendo el patrón de PlansPage
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; // mismo valor que PlansPage

    const filterHook = useFilter('cita');
    const { activeFilter, clearFilter, dismissDefaultFilter, resetFilters, loadFilters } = filterHook;

    const handleCitaEvent = useCallback((action: string, citaData: any) => {
        if (action === 'create') {
            setCitas(prev => {
                const exists = prev.some(c => c.id === citaData.id);
                if (exists) return prev;
                return [citaData, ...prev];
            });
        } else if (action === 'update') {
            setCitas(prev => prev.map(c => c.id === citaData.id ? citaData : c));
        } else if (action === 'delete') {
            setCitas(prev => prev.filter(c => c.id !== citaData.id));
        }
    }, []);

    useWebSocketCitas(handleCitaEvent);

    const loadData = useCallback(async () => {
        if (!perm?.can_read) {
            setCitas([]);
            setVentas([]);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const [citasData, ventasData] = await Promise.all([
                fetchCitas(),
                fetchVentasCafeteria()
            ]);
            setCitas(citasData);
            setVentas(ventasData);
            await loadFilters();
        } catch (e) { /* Browser diagnostics omitted. */ }
        finally { setIsLoading(false); }
    }, [perm?.can_read, loadFilters]);

    useEffect(() => {
        if (!initialLoadRef.current && perm !== null) {
            loadData();
            initialLoadRef.current = true;
        }
    }, [loadData, perm]);

    // Debounce búsqueda
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(filterState.searchTerm), 400);
        return () => clearTimeout(t);
    }, [filterState.searchTerm]);

    useEffect(() => {
        if (activeFilter === null) {
            setFilterState({ searchTerm: '', sucursalFilter: null, estadoFilter: null, estadoPagoFilter: null });
            setDebouncedSearch('');
        }
    }, [activeFilter]);

    useEffect(() => {
        if (activeFilter) {
            const p = activeFilter.parameters;
            setFilterState(prev => ({ ...prev, sucursalFilter: p.sucursal_id || null, estadoFilter: p.estado || null, estadoPagoFilter: p.estado_pago || null }));
        }
    }, [activeFilter]);

    // Resetear página a 1 cuando cambian los filtros, tipo o datos subyacentes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, filterState.sucursalFilter, filterState.estadoFilter, filterState.estadoPagoFilter, tipoFilter, citas.length, ventas.length]);

    const handleSearchChange = useCallback((v: string) => { setFilterState(p => ({ ...p, searchTerm: v })); if (activeFilter) clearFilter(); }, [activeFilter, clearFilter]);
    const handleSucursalChange = useCallback((v: string) => { setFilterState(p => ({ ...p, sucursalFilter: v ? Number(v) : null })); if (activeFilter) clearFilter(); }, [activeFilter, clearFilter]);
    const handleEstadoChange = useCallback((v: string) => { setFilterState(p => ({ ...p, estadoFilter: v || null })); if (activeFilter) clearFilter(); }, [activeFilter, clearFilter]);
    const handleEstadoPagoChange = useCallback((v: string) => { setFilterState(p => ({ ...p, estadoPagoFilter: v || null })); if (activeFilter) clearFilter(); }, [activeFilter, clearFilter]);
    const handleClearAll = useCallback(() => {
        setFilterState({ searchTerm: '', sucursalFilter: null, estadoFilter: null, estadoPagoFilter: null });
        setDebouncedSearch('');
        if (activeFilter) clearFilter();
        dismissDefaultFilter(); resetFilters();
    }, [activeFilter, clearFilter, dismissDefaultFilter, resetFilters]);

    const filterOptions = useMemo((): ReservasFilterOptions => {
        const sMap = new Map<number, string>();
        const eSet = new Set<string>();
        const epSet = new Set<string>();

        citas.forEach(c => {
            if (c.sucursal && c.sucursal_info?.nombre) sMap.set(c.sucursal, c.sucursal_info.nombre);
            if (c.estado) eSet.add(c.estado);
            if (c.estado_pago) epSet.add(c.estado_pago);
        });

        // Agregar sucursales de ventas
        ventas.forEach(v => {
            if (v.sucursal && v.sucursal_info?.nombre) sMap.set(v.sucursal, v.sucursal_info.nombre);
        });

        return {
            sucursales: Array.from(sMap.entries()).sort((a, b) => a[1].localeCompare(b[1])),
            estados: Array.from(eSet).sort().map(e => [e, e]),
            estadosPago: Array.from(epSet).sort().map(e => [e, e]),
        };
    }, [citas, ventas]);

    // Lista unificada completa (sin paginar)
    const unifiedItems = useMemo<UnifiedItem[]>(() => {
        const { sucursalFilter, estadoFilter, estadoPagoFilter } = filterState;
        const term = debouncedSearch.toLowerCase();

        const citasFiltradas = citas
            .filter(c => {
                const matchesSearch = !term ||
                    `${c.cliente_info?.first_name || ''} ${c.cliente_info?.last_name || ''}`.toLowerCase().includes(term) ||
                    (c.codigo || '').toLowerCase().includes(term) ||
                    (c.estado || '').toLowerCase().includes(term) ||
                    (c.estado_pago || '').toLowerCase().includes(term) ||
                    (c.sucursal_info?.nombre || '').toLowerCase().includes(term);
                return matchesSearch
                    && (!sucursalFilter || c.sucursal === sucursalFilter)
                    && (!estadoFilter || (c.estado || '').toLowerCase() === estadoFilter.toLowerCase())
                    && (!estadoPagoFilter || (c.estado_pago || '').toLowerCase() === estadoPagoFilter.toLowerCase());
            })
            .map(c => ({ ...c, type: 'cita' as const }));

        const ventasFiltradas = ventas
            .filter(v => {
                const matchesSearch = !term ||
                    (v.codigo || '').toLowerCase().includes(term) ||
                    (v.estado_pago || '').toLowerCase().includes(term) ||
                    (v.tipo_comprobante || '').toLowerCase().includes(term) ||
                    (v.observaciones || '').toLowerCase().includes(term) ||
                    (v.cliente_info
                        ? `${v.cliente_info.first_name} ${v.cliente_info.last_name}`.toLowerCase().includes(term)
                        : false);
                // Aplicar filtro de sucursal
                return matchesSearch
                    && (!sucursalFilter || v.sucursal === sucursalFilter);  // <-- añadir esta línea
            })
            .map(v => ({ ...v, type: 'venta' as const }));

        if (tipoFilter === 'citas') return citasFiltradas.sort((a, b) => b.id - a.id);
        if (tipoFilter === 'ventas') return ventasFiltradas.sort((a, b) => b.id - a.id);

        return [...citasFiltradas, ...ventasFiltradas].sort((a, b) => b.id - a.id);
    }, [citas, ventas, debouncedSearch, filterState, tipoFilter]);

    // Paginación: cálculo de páginas y elementos mostrados
    const totalPages = Math.ceil(unifiedItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedItems = unifiedItems.slice(startIndex, startIndex + itemsPerPage);

    const hasActiveFilters = !!(filterState.searchTerm || filterState.sucursalFilter || filterState.estadoFilter || filterState.estadoPagoFilter || activeFilter || tipoFilter !== 'todos');

    // Estadísticas (sin cambios)
    const statsCitas = useMemo(() => ({
        total: citas.length,
        confirmadas: citas.filter(c => c.estado?.toLowerCase() === 'confirmada').length,
        pendientes: citas.filter(c => c.estado?.toLowerCase() === 'pendiente').length,
        canceladas: citas.filter(c => c.estado?.toLowerCase() === 'cancelada').length,
    }), [citas]);

    const statsVentas = useMemo(() => ({
        total: ventas.length,
        pagadas: ventas.filter(v => v.estado_pago === 'pagado').length,
        pendientes: ventas.filter(v => v.estado_pago === 'pendiente').length,
        canceladas: ventas.filter(v => v.estado_pago === 'cancelado').length,
    }), [ventas]);

    // ⏳ Cargando permisos
    if (perm === null) return <Loading text="Cargando reservas..." />;

    // 🚫 Sin permiso de lectura
    if (!perm.can_read) {
        return (
            <div className="rp-page" style={{ background: '#fff', minHeight: '100vh', padding: 'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>
                <div className="rp-fadeup" style={{ marginBottom: '28px' }}>
                    <nav style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '.72rem', fontWeight: '600', color: '#94a3b8', marginBottom: '12px' }}>
                        <a href="/dashboard" style={{ color: '#94a3b8', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '3px' }}><Home size={11} /> Dashboard</a>
                        <ChevronRight size={10} color="#e2e8f0" />
                        <span style={{ color: colors.primary, fontWeight: '700' }}>Reservas</span>
                    </nav>
                    <h1 style={{ fontSize: 'clamp(1.5rem,3vw,2rem)', fontWeight: '900', color: '#0f172a' }}>Panel de <span style={{ color: colors.primary }}>Reservas</span></h1>
                </div>
                <div style={{ background: '#fff', borderRadius: '20px', border: '1.5px solid #f1f5f9', padding: '60px 24px', textAlign: 'center' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: `${colors.primary}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                        <XCircle size={28} color={colors.primary} />
                    </div>
                    <p style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', marginBottom: '6px' }}>Acceso denegado</p>
                    <p style={{ fontSize: '.85rem', color: '#94a3b8' }}>No tienes permiso para ver las reservas.</p>
                </div>
            </div>
        );
    }

    // ✅ Vista principal
    return (
        <>
            <style>{css}</style>
            <div className="rp-page" style={{ background: '#fff', minHeight: '100vh', padding: 'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>
                {/* Encabezado */}
                <div className="rp-fadeup" style={{ marginBottom: '28px' }}>
                    <nav style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '.72rem', fontWeight: '600', color: '#94a3b8', marginBottom: '12px' }}>
                        <a href="/dashboard" style={{ color: '#94a3b8', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '3px' }} onMouseEnter={e => e.currentTarget.style.color = colors.primary} onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}><Home size={11} /> Dashboard</a>
                        <ChevronRight size={10} color="#e2e8f0" />
                        <span style={{ color: colors.primary, fontWeight: '700' }}>Citas & Ventas</span>
                    </nav>
                    <h1 style={{ fontSize: 'clamp(1.5rem,3vw,2rem)', fontWeight: '900', color: '#0f172a', letterSpacing: '-.025em', lineHeight: 1.1, marginBottom: '6px' }}>
                        Panel de <span style={{ color: colors.primary }}>Reservas</span> y <span style={{ color: colors.accentDark }}>Ventas</span>
                    </h1>
                    <p style={{ fontSize: '.85rem', color: '#64748b', fontWeight: '500' }}>Gestiona todas las citas y ventas de productos desde un solo lugar</p>
                    <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
                        {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                            <div key={i} style={{ height: '3px', width: i === 0 ? '24px' : '8px', borderRadius: '3px', background: c }} />
                        ))}
                    </div>
                </div>

                {/* Selector de tipo */}
                <div className="rp-fadeup-1" style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {(['todos', 'citas', 'ventas'] as const).map(tipo => (
                        <button
                            key={tipo}
                            onClick={() => setTipoFilter(tipo)}
                            style={{
                                padding: '8px 18px',
                                borderRadius: '10px',
                                border: `1.5px solid ${tipoFilter === tipo ? colors.brand : '#e2e8f0'}`,
                                background: tipoFilter === tipo ? `${colors.brand}12` : '#fff',
                                color: tipoFilter === tipo ? colors.brand : '#64748b',
                                fontWeight: '700',
                                fontSize: '.78rem',
                                cursor: 'pointer',
                                transition: 'all .2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            {tipo === 'citas' ? <Calendar size={14} /> : tipo === 'ventas' ? <ShoppingBag size={14} /> : <Coffee size={14} />}
                            {tipo === 'todos' ? 'Todos' : tipo === 'citas' ? 'Citas' : 'Ventas'}
                        </button>
                    ))}
                </div>

                {/* Estadísticas */}
                <div className="rp-fadeup-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: '10px', marginBottom: '24px' }}>
                    {/* Citas */}
                    <div style={{ background: '#fff', borderRadius: '14px', border: '1.5px solid #f1f5f9', padding: '12px 14px' }}>
                        <span style={{ fontSize: '.65rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em' }}>Citas Total</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: '900', color: colors.brand, lineHeight: 1 }}>{statsCitas.total}</span>
                    </div>
                    <div style={{ background: '#fff', borderRadius: '14px', border: '1.5px solid #f1f5f9', padding: '12px 14px' }}>
                        <span style={{ fontSize: '.65rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em' }}>Citas Confirmadas</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: '900', color: colors.secondaryLight, lineHeight: 1 }}>{statsCitas.confirmadas}</span>
                    </div>
                    <div style={{ background: '#fff', borderRadius: '14px', border: '1.5px solid #f1f5f9', padding: '12px 14px' }}>
                        <span style={{ fontSize: '.65rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em' }}>Citas Pendientes</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: '900', color: colors.accent2, lineHeight: 1 }}>{statsCitas.pendientes}</span>
                    </div>
                    {/* Ventas */}
                    <div style={{ background: '#fff', borderRadius: '14px', border: '1.5px solid #f1f5f9', padding: '12px 14px' }}>
                        <span style={{ fontSize: '.65rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em' }}>Ventas Total</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: '900', color: colors.accent1, lineHeight: 1 }}>{statsVentas.total}</span>
                    </div>
                    <div style={{ background: '#fff', borderRadius: '14px', border: '1.5px solid #f1f5f9', padding: '12px 14px' }}>
                        <span style={{ fontSize: '.65rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em' }}>Ventas Pagadas</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: '900', color: colors.secondaryLight, lineHeight: 1 }}>{statsVentas.pagadas}</span>
                    </div>
                    <div style={{ background: '#fff', borderRadius: '14px', border: '1.5px solid #f1f5f9', padding: '12px 14px' }}>
                        <span style={{ fontSize: '.65rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em' }}>Ventas Pendientes</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: '900', color: colors.accent2, lineHeight: 1 }}>{statsVentas.pendientes}</span>
                    </div>
                </div>

                {/* Filtros */}
                <div className="rp-fadeup-1" style={{ background: '#fff', borderRadius: '20px', border: '1.5px solid #f1f5f9', boxShadow: '0 4px 24px rgba(0,0,0,.04)', padding: '20px 24px', marginBottom: '24px' }}>
                    <ReservasFilter
                        filterState={filterState}
                        options={filterOptions}
                        onSearchChange={handleSearchChange}
                        onSucursalChange={handleSucursalChange}
                        onEstadoChange={handleEstadoChange}
                        onEstadoPagoChange={handleEstadoPagoChange}
                        onClearAll={handleClearAll}
                        totalFiltered={unifiedItems.length}
                        totalAll={citas.length + ventas.length}
                        filterHook={filterHook}
                    />
                </div>

                {/* Listado paginado */}
                {isLoading ? (
                    <div className="rp-fadeup-2" style={{ background: '#fff', borderRadius: '20px', border: '1.5px solid #f1f5f9', padding: '60px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
                        <div style={{ width: '40px', height: '40px', border: `3px solid ${colors.brand}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
                        <p style={{ color: '#64748b', fontWeight: '600' }}>Cargando reservas y ventas...</p>
                    </div>
                ) : unifiedItems.length === 0 ? (
                    <div className="rp-fadeup-2" style={{ background: '#fff', borderRadius: '20px', border: '1.5px solid #f1f5f9', padding: '60px 24px', textAlign: 'center' }}>
                        <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: `${colors.brand}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <Search size={24} color={colors.brand} style={{ opacity: .6 }} />
                        </div>
                        <p style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', marginBottom: '6px' }}>
                            {hasActiveFilters ? 'Sin resultados con esos filtros' : 'No hay registros aún'}
                        </p>
                        <p style={{ fontSize: '.85rem', color: '#94a3b8', marginBottom: '20px' }}>
                            {hasActiveFilters ? 'Ajusta los filtros o cambia el tipo para ver más resultados.' : 'Las citas y ventas aparecerán aquí cuando se registren.'}
                        </p>
                        {hasActiveFilters && (
                            <button onClick={handleClearAll} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 20px', borderRadius: '11px', background: `linear-gradient(135deg,${colors.brand},${colors.accent1})`, color: '#fff', border: 'none', fontWeight: '700', fontSize: '.85rem', cursor: 'pointer', boxShadow: `0 4px 14px ${colors.brand}28` }}>
                                Limpiar todos los filtros
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="rp-fadeup-2 custom-scroll" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: '16px' }}>
                            {displayedItems.map(item => {
                                if (item.type === 'cita') return <CitaCard key={`cita-${item.id}`} cita={item} />;
                                return <VentaCard key={`venta-${item.id}`} venta={item} />;
                            })}
                        </div>
                        {/* Paginación – exactamente igual que en PlansPage */}
                        {unifiedItems.length > 0 && (
                            <div style={{ borderTop: '1.5px solid #f1f5f9', marginTop: '24px', paddingTop: '20px' }}>
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    itemsPerPage={itemsPerPage}
                                    totalItems={unifiedItems.length}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default ReservasPage;